/**
 * What usually happens in the first weeks after a positive result, and when.
 *
 * Reviewed, and the scope of that review is the important part — see
 * `EARLY_PREGNANCY_MILESTONES_REVIEW`. A clinician read the four windows and
 * confirmed they are not aberrant. That is a plausibility check, not an
 * endorsement of any one clinic's schedule, and the surface is worded to match:
 * every row is what usually happens, and the section says on its face that the
 * patient's own clinic decides. Widening the claim beyond that — a row phrased
 * as an instruction, a window presented as her appointment — would outrun what
 * was actually checked.
 *
 * Why it exists at all: Home already tells her a repeat beta comes "often two
 * days later" and a first scan "usually a few weeks after the result", which is
 * true and unusable — she cannot put "a few weeks" in a calendar. The API dates
 * her pregnancy (`gestationalWeeks`, `estimatedDueDate`), and
 * `gestationalDates.ts` turns a week into calendar days. The only missing piece
 * is which weeks, and that is a clinical claim.
 *
 * How to read a row. `timing` is deliberately of two kinds, because these
 * milestones are not anchored to the same thing: a repeat beta is timed from the
 * result she just recorded, a scan from how far along she is. Collapsing both
 * into gestational weeks would have invented precision for one of them.
 *
 * `sourceToVerify` names what a reviewer has to confirm, and carries no citation
 * on purpose. A plausible-looking reference is worse than an admitted gap: it
 * invites the reviewer to trust it. Where the claim is already live in product
 * copy that shipped, the row says so, because that is a different question — it
 * is already being told to patients — and it should be reviewed with the rest
 * rather than grandfathered.
 *
 * Nothing here is a threshold, a diagnosis, or a reason to act. Every row says
 * what the clinic usually does and when it usually happens; the clinic's own
 * plan is what counts, and the copy says that too.
 */

/**
 * What was checked, by whom, and how far it goes.
 *
 * Recorded rather than remembered: in six months the only way to know whether
 * "reviewed" meant *these are the right windows for this clinic* or *these are
 * not absurd* will be this record. It is the second.
 *
 * The reviewer is deliberately unnamed — they reviewed as a favour, not in a
 * professional capacity, and attaching a clinician's name to patient-facing
 * timing would claim more than they agreed to.
 */
export const EARLY_PREGNANCY_MILESTONES_REVIEW = {
  reviewedOn: "2026-08-18",
  reviewer: "Anonymous clinician colleague, relayed by the product owner",
  scope:
    "Plausibility only: that the four windows are not aberrant as general indications. Not an endorsement of any clinic's schedule, and not a review of whether the app should show dates at all.",
  corrections: "None. All four windows accepted as proposed.",
} as const;

/**
 * The switch that publishes this content.
 *
 * Deliberately one boolean and not a feature flag service: the decision it
 * encodes is a fact about the content, not a rollout.
 */
export const EARLY_PREGNANCY_MILESTONES_REVIEWED = true;

/**
 * When a milestone happens, in the terms it is actually timed by.
 *
 * `days-after-result` counts from the recorded beta, `gestational-weeks` from
 * how far along she is. Both are inclusive ranges.
 */
export type MilestoneTiming =
  | { anchor: "days-after-result"; fromDay: number; toDay: number }
  | { anchor: "gestational-weeks"; fromWeek: number; toWeek: number };

export type EarlyPregnancyMilestone = {
  /** Stable id; the copy keys are derived from it. */
  id: string;
  timing: MilestoneTiming;
  /**
   * What a reviewer must confirm before this may be shown. No citation by
   * design — see the file comment.
   */
  sourceToVerify: string;
  /** True when the claim is already in shipped product copy, in vaguer terms. */
  alreadyStatedVaguely: boolean;
};

/**
 * The proposal, in the order she would meet them.
 *
 * Four rows, and the restraint is the point: every additional milestone is
 * another claim to defend, and a list long enough to feel complete reads as
 * *the* schedule — which is the clinic's to give, not ours.
 */
export const PROPOSED_EARLY_PREGNANCY_MILESTONES: readonly EarlyPregnancyMilestone[] =
  [
    {
      id: "repeat_beta",
      timing: { anchor: "days-after-result", fromDay: 2, toDay: 3 },
      sourceToVerify:
        "Whether a repeat beta is routinely drawn 48-72h after the first, and whether we may state that as a range rather than the clinic's own instruction.",
      alreadyStatedVaguely: true,
    },
    {
      id: "first_scan",
      timing: { anchor: "gestational-weeks", fromWeek: 6, toWeek: 7 },
      sourceToVerify:
        "The usual window for the first ultrasound after ART, and whether it is stated in gestational weeks rather than days after transfer.",
      alreadyStatedVaguely: true,
    },
    {
      id: "care_handover",
      timing: { anchor: "gestational-weeks", fromWeek: 8, toWeek: 10 },
      sourceToVerify:
        "When fertility clinics typically hand over to obstetric care, and whether a window can be given at all when it varies by clinic and by country.",
      alreadyStatedVaguely: true,
    },
    {
      id: "first_trimester_screening",
      timing: { anchor: "gestational-weeks", fromWeek: 11, toWeek: 14 },
      sourceToVerify:
        "The first-trimester screening window, and whether it belongs on an early-pregnancy surface at all or after the handover it follows.",
      alreadyStatedVaguely: false,
    },
  ];

/**
 * The milestones that may be shown.
 *
 * Empty until the content is reviewed. Callers must render nothing rather than
 * a heading with no rows under it — an empty "what comes next" section promises
 * something and delivers a gap.
 */
export function earlyPregnancyMilestones(): readonly EarlyPregnancyMilestone[] {
  return EARLY_PREGNANCY_MILESTONES_REVIEWED
    ? PROPOSED_EARLY_PREGNANCY_MILESTONES
    : [];
}
